import { uniq } from "lodash-es";

import { isDefined, mergeLeft } from "@utils/helpers";
import {
    SearchComponentEnum,
    SearchComponents,
} from "@utils/search/@types/searchComponent";
import {
    PaginationConfiguration,
    SearchComponentConfiguration,
} from "@utils/search/@types/searchConfiguration";
import {
    ComponentSearchResult,
    PaginationResult,
    ResultListResult,
} from "@utils/search/@types/searchResult";
import { SearchComponentState } from "@utils/search/@types/searchState";
import { getDefaultComponentConfiguration } from "@utils/search/defaultConfiguration";
import { isType } from "@utils/search/type.utils";

const isComponent =
    (component: SearchComponentEnum) =>
    (componentConfiguration: SearchComponentConfiguration) =>
        componentConfiguration.component === component;

const tryMergeResultListResult = (
    result: ComponentSearchResult,
    state?: SearchComponentState,
) => {
    if (!isType<ResultListResult>(result, "RESULT_LIST")) {
        return;
    }

    return !!state && isType<ResultListResult>(state, "RESULT_LIST")
        ? {
              ...state,
              hits: [...state.hits, ...result.hits],
              queryID: result.queryID,
          }
        : result;
};

const tryMergePaginationConfiguration = (
    configuration: SearchComponentConfiguration,
    result: ComponentSearchResult,
) => {
    if (
        !isType<PaginationConfiguration>(configuration, "PAGINATION") ||
        !isType<PaginationResult>(result, "PAGINATION")
    ) {
        return;
    }

    return {
        ...configuration,
        component: result.component,
        currentPage: result.currentPage,
        page: result.currentPage > 0 ? result.currentPage : 0,
    };
};

const mergeResultsInConfiguration: (
    configurations: Array<SearchComponentConfiguration>,
    previousConfigurations: Array<SearchComponentConfiguration>,
    results: Array<ComponentSearchResult>,
) => Array<SearchComponentConfiguration> = (
    configurations,
    previousConfigurations,
    results,
) =>
    SearchComponents.ALL.map(
        searchComponent =>
            configurations.find(isComponent(searchComponent)) ??
            previousConfigurations.find(isComponent(searchComponent)) ??
            getDefaultComponentConfiguration(searchComponent),
    )
        .map(configuration => {
            const matchedResult = results.find(
                result => result.component === configuration.component,
            );
            if (!matchedResult) return configuration;

            return (
                tryMergePaginationConfiguration(configuration, matchedResult) ||
                mergeLeft(configuration, matchedResult)
            );
        })
        .filter(isDefined);

const mergeSearchResultsInState: (
    states: Array<SearchComponentState>,
    results: Array<ComponentSearchResult>,
    invalidations: Array<SearchComponentEnum>,
) => Array<SearchComponentState> = (states, results, invalidations) => {
    const components = uniq([
        ...states.map(state => state.component),
        ...results.map(state => state.component),
    ]);

    return components.map(component => {
        const state = states.find(
            componentState =>
                componentState.component === component &&
                !invalidations.includes(component),
        );
        const result = results.find(
            componentState => componentState.component === component,
        );

        if (!state) return result!;
        if (!result) return state!;

        return (
            tryMergeResultListResult(result, state) || mergeLeft(state, result)
        );
    });
};

export { mergeResultsInConfiguration, mergeSearchResultsInState };
